<script setup>
import { shallowRef, watch } from 'vue'
import ClassicAuthCard from '../components/ClassicAuthCard.vue'

const props = defineProps({
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  isCoolingDown: { type: Boolean, default: false },
  countdown: { type: String, default: '' },
  isAdmin: { type: Boolean, default: false },
  registerUrl: { type: String, default: '/register' },
  termsLink: { type: String, default: '' },
  privacyLink: { type: String, default: '' },
})

const emit = defineEmits(['submit', 'resend', 'back', 'clearError'])
const code = shallowRef('')

watch(() => props.error, (error) => {
  if (error) code.value = ''
})

function submit() {
  if (!code.value || props.loading) return
  emit('submit', code.value)
}

function updateCode(event) {
  code.value = event.target.value.replace(/\D/g, '').slice(0, 8)
  emit('clearError')
}
</script>

<template>
  <ClassicAuthCard
    :title="isAdmin ? 'Admin Login' : 'Login'"
    :is-admin="isAdmin"
    :register-url="registerUrl"
  >
    <form class="classic-code" :class="{ 'classic-code--admin': isAdmin }" @submit.prevent="submit">
      <label class="classic-code__label" for="classic-code">Code *</label>
      <input
        id="classic-code"
        :value="code"
        class="classic-code__input"
        :class="{ 'classic-code__input--invalid': error }"
        name="code"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="8"
        placeholder="######"
        required
        @input="updateCode"
      >
      <p v-if="error" class="classic-code__error" role="alert">{{ error }}</p>

      <div class="classic-code__links">
        <button
          class="classic-code__link"
          type="button"
          :disabled="loading || isCoolingDown"
          @click="emit('resend')"
        >
          {{ isCoolingDown ? `Resend code in ${countdown}` : 'Resend code' }}
        </button>

        <button class="classic-code__link" type="button" :disabled="loading" @click="emit('back')">
          Login with email
        </button>
      </div>

      <p v-if="!isAdmin" class="classic-code__terms">
        By continuing, you agree with the
        <a v-if="termsLink" :href="termsLink" target="_blank" rel="noopener">Terms of Service</a>,
        acknowledge the
        <a v-if="privacyLink" :href="privacyLink" target="_blank" rel="noopener">Privacy Policy</a>
        and agree to receive text messages from
        <a href="https://remonihealth.com/" target="_blank" rel="noopener">RemoniHealth</a>.
      </p>

      <button class="classic-code__submit" type="submit" :disabled="loading || !code">
        {{ loading ? 'Please wait…' : 'Login' }}
      </button>
    </form>
  </ClassicAuthCard>
</template>

<style scoped>
.classic-code {
  display: flex;
  flex-direction: column;
}

.classic-code__label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}

.classic-code__input {
  background: transparent;
  border: 0;
  border-bottom: 1px solid #c6c6c6;
  border-radius: 0;
  box-sizing: border-box;
  color: #7c7c7c;
  font: inherit;
  font-size: 22px;
  letter-spacing: 0.25em;
  min-height: 50px;
  padding: 10px 14px;
  text-align: center;
  width: 100%;
}

.classic-code__input::placeholder {
  color: #7c7c7c;
  opacity: 0.75;
}

.classic-code__input:focus {
  border-color: #564495;
  box-shadow: none;
  outline: none;
}

.classic-code__input--invalid {
  border-color: #dc3545;
}

.classic-code__error {
  color: #dc3545;
  font-size: 13px;
  margin: 7px 0 0;
}

.classic-code__submit {
  align-self: center;
  background: #ff7f5d;
  border: 0;
  border-radius: 24px;
  color: #fff;
  cursor: pointer;
  font: inherit;
  font-weight: 500;
  min-width: 230px;
  padding: 12px 20px;
}

.classic-code__links {
  display: flex;
  justify-content: space-between;
  margin: 15px 0;
}

.classic-code__link {
  background: none;
  border: 0;
  color: #564495;
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  padding: 0;
}

.classic-code__link:last-child {
  text-align: right;
}

.classic-code__terms {
  color: #7c7c7c;
  font-size: 14px;
  line-height: 1.5;
  margin: 18px 0;
  text-align: center;
}

.classic-code__terms a {
  color: #564495;
  text-decoration: none;
}

.classic-code__submit:disabled,
.classic-code__link:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.classic-code--admin .classic-code__input:focus {
  border-color: #56dbcb;
}

.classic-code--admin .classic-code__submit {
  background: #56dbcb;
}
</style>
